import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, StatCard } from "../components/ui-kit";
import { ShoppingCart, IndianRupee, Users2, Package as PackageIcon, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { useAuth } from "../lib/auth";

const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [achievement, setAchievement] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const [tva, setTva] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [s, a, n, t] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/targets/achievement").catch(() => ({ data: null })),
        api.get("/notifications").catch(() => ({ data: [] })),
        api.get("/dashboard/target-vs-achievement").catch(() => ({ data: null })),
      ]);
      if (cancelled) return;
      setStats(s.data);
      setAchievement(a.data);
      setNotifications(n.data.slice(0, 6));
      setTva(t.data);
    })();
    return () => {
      cancelled = true;
    };
    // Loads once on mount; downstream pages refresh their own data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusData = stats
    ? [
        { name: "Pending", value: stats.by_status?.pending || 0, color: "#DDA15E" },
        { name: "Approved", value: stats.by_status?.approved || 0, color: "#A3B18A" },
        { name: "Dispatched", value: stats.by_status?.dispatched || 0, color: "#1E3F20" },
      ]
    : [];

  const orderTargets = achievement?.order_targets || [];
  const orderChart = orderTargets.slice(0, 8).map((t) => ({
    name: (t.customer_name || t.customer_id || "").slice(0, 10),
    Target: t.target_qty || 0,
    Achieved: t.achieved_qty || 0,
  }));

  return (
    <div>
      <PageHeader title={`Welcome, ${user?.name?.split(" ")[0] || "Team"}`} subtitle="A snapshot of your field operations today" />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard
          label="Orders this month"
          value={stats?.orders_month ?? "—"}
          accent="forest"
          hint={`${stats?.orders_total || 0} total`}
          onClick={() => navigate("/orders")}
          testid="stat-orders-month"
        />
        <StatCard
          label="Order Value (MTD)"
          value={money(stats?.orders_value_month)}
          accent="moss"
          onClick={() => navigate("/orders")}
          testid="stat-orders-value"
        />
        <StatCard
          label="Collections (MTD)"
          value={money(stats?.collections_month)}
          accent="ochre"
          onClick={() => navigate("/collections")}
          testid="stat-collections"
        />
        <StatCard
          label="Total Outstanding"
          value={money(stats?.total_outstanding)}
          accent="terra"
          hint={stats?.total_over_3m > 0 ? `${money(stats.total_over_3m)} over 3M` : "Bills Receivable"}
          onClick={() => navigate("/br-updates")}
          testid="stat-outstanding"
        />
        <StatCard
          label="Customers"
          value={stats?.total_customers ?? "—"}
          accent="terra"
          hint={stats?.total_users ? `${stats.total_users} team members` : ""}
          onClick={() => navigate("/customers")}
          testid="stat-customers"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C6B5E]">Target vs Achievement</div>
              <h3 className="font-heading text-xl font-bold mt-1">
                {tva?.view === "team" ? "Team performance — this month" : "Orders & Collections — this month"}
              </h3>
            </div>
            <TrendingUp className="w-5 h-5 text-[#1E3F20]" />
          </div>
          <TargetVsAchievement tva={tva} />
          {tva?.view === "self" && orderChart.length > 0 && (
            <div className="h-56 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={orderChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                  <XAxis dataKey="name" stroke="#5C6B5E" fontSize={11} />
                  <YAxis stroke="#5C6B5E" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E5E2DC", borderRadius: 8 }} />
                  <Legend />
                  <Bar dataKey="Target" fill="#DDA15E" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Achieved" fill="#1E3F20" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C6B5E]">Pipeline</div>
          <h3 className="font-heading text-xl font-bold mt-1 mb-3">Orders by status</h3>
          {statusData.every((d) => d.value === 0) ? (
            <div className="text-sm text-[#5C6B5E] py-10 text-center">No orders yet.</div>
          ) : (
            <>
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      onClick={(entry) => navigate(`/orders?status=${entry.name.toLowerCase()}`)}
                      style={{ cursor: "pointer" }}
                    >
                      {statusData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-2 justify-center mt-2" data-testid="status-drilldown">
                {statusData.map((d) => (
                  <button
                    key={d.name}
                    type="button"
                    onClick={() => navigate(`/orders?status=${d.name.toLowerCase()}`)}
                    className="text-xs px-2.5 py-1 rounded-full border border-[#E5E2DC] hover:border-[#1E3F20]/40 hover:bg-[#F2EDE4]"
                    data-testid={`pipe-${d.name.toLowerCase()}`}
                  >
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle"
                      style={{ background: d.color }}
                    />
                    {d.name} ({d.value})
                  </button>
                ))}
              </div>
            </>
          )}
        </Card>

        <Card className="lg:col-span-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C6B5E]">Inbox</div>
          <h3 className="font-heading text-xl font-bold mt-1 mb-3">Notifications</h3>
          {notifications.length === 0 ? (
            <div className="text-sm text-[#5C6B5E]">No notifications.</div>
          ) : (
            <ul className="space-y-3">
              {notifications.map((n) => (
                <li key={n.id} className="border-l-2 border-[#A3B18A] pl-3">
                  <div className="text-sm font-semibold">{n.title}</div>
                  <div className="text-xs text-[#5C6B5E]">{n.message}</div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function TargetVsAchievement({ tva }) {
  if (!tva) return <div className="text-sm text-[#5C6B5E] py-6 text-center">Loading…</div>;
  const t = tva.totals || {};
  const orderPct = t.order_target_value > 0 ? Math.min(100, (t.order_achieved_value / t.order_target_value) * 100) : 0;
  const collPct = t.collection_target_amount > 0 ? Math.min(100, (t.collection_achieved_amount / t.collection_target_amount) * 100) : 0;
  const tile = (label, target, achieved, pct, accent) => (
    <div className="rounded-xl border border-[#E5E2DC] p-4 bg-white">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C6B5E]">{label}</div>
      <div className="font-heading text-2xl font-black text-[#1A2E1C] mt-1">{money(achieved)}</div>
      <div className="text-xs text-[#5C6B5E] mt-0.5">of {money(target)} target</div>
      <div className="w-full h-2 mt-3 rounded-full bg-[#F2EDE4] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
      </div>
      <div className="text-[10px] font-bold mt-1" style={{ color: accent }}>{pct.toFixed(0)}%</div>
    </div>
  );
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="tva-tiles">
        {tile("Order Target vs Achieved", t.order_target_value || 0, t.order_achieved_value || 0, orderPct, "#1E3F20")}
        {tile("Collection Target vs Achieved", t.collection_target_amount || 0, t.collection_achieved_amount || 0, collPct, "#C15A2C")}
      </div>
      {tva.view === "team" && (!tva.members || tva.members.filter((m) => m.order_target_value || m.order_achieved_value || m.collection_target_amount || m.collection_achieved_amount).length === 0) && (
        <div className="mt-4 text-sm text-[#5C6B5E] py-6 text-center border border-dashed border-[#E5E2DC] rounded-lg" data-testid="tva-empty-hint">
          No targets set for your team this month yet. Upload targets on the Targets page, and make sure your sales team has <b>Reports To</b> assigned on the Users page.
        </div>
      )}
      {tva.view === "team" && tva.members?.length > 0 && tva.members.some((m) => m.order_target_value || m.order_achieved_value || m.collection_target_amount || m.collection_achieved_amount) && (
        <div className="mt-4 overflow-x-auto" data-testid="tva-team-table">
          <table className="min-w-full text-xs">
            <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C6B5E]">
                <th className="px-3 py-2">Sales person</th>
                <th className="px-3 py-2 text-right">Order target</th>
                <th className="px-3 py-2 text-right">Order achieved</th>
                <th className="px-3 py-2 text-right">%</th>
                <th className="px-3 py-2 text-right">Coll. target</th>
                <th className="px-3 py-2 text-right">Coll. achieved</th>
                <th className="px-3 py-2 text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {tva.members.map((m) => {
                const op = m.order_target_value > 0 ? Math.min(100, (m.order_achieved_value / m.order_target_value) * 100) : 0;
                const cp = m.collection_target_amount > 0 ? Math.min(100, (m.collection_achieved_amount / m.collection_target_amount) * 100) : 0;
                const hasAny = m.order_target_value || m.order_achieved_value || m.collection_target_amount || m.collection_achieved_amount;
                if (!hasAny) return null;
                return (
                  <tr key={m.user_id} className="border-b border-[#E5E2DC] hover:bg-[#F2EDE4]/60" data-testid={`tva-row-${m.user_id}`}>
                    <td className="px-3 py-2 font-semibold">
                      {m.user_name || m.user_email}
                      {tva.self_id === m.user_id && <span className="text-[10px] text-[#5C6B5E] font-normal ml-1">(You)</span>}
                    </td>
                    <td className="px-3 py-2 text-right">{money(m.order_target_value)}</td>
                    <td className="px-3 py-2 text-right">{money(m.order_achieved_value)}</td>
                    <td className="px-3 py-2 text-right font-bold" style={{ color: op >= 100 ? "#1E3F20" : "#C15A2C" }}>{op.toFixed(0)}%</td>
                    <td className="px-3 py-2 text-right">{money(m.collection_target_amount)}</td>
                    <td className="px-3 py-2 text-right">{money(m.collection_achieved_amount)}</td>
                    <td className="px-3 py-2 text-right font-bold" style={{ color: cp >= 100 ? "#1E3F20" : "#C15A2C" }}>{cp.toFixed(0)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

